import React, { useState, useEffect, useRef } from 'react';
import { get } from 'aws-amplify/api';
import { getLocalDateString } from '../utils/date';

// 経過時間を HH:MM:SS 形式でフォーマット
function formatElapsed(startAt) {
  const seconds = Math.floor((Date.now() - new Date(startAt).getTime()) / 1000);
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return [h, m, s].map(v => String(v).padStart(2, '0')).join(':');
}

// HH:MM 形式でフォーマット
function formatHHMM(date) {
  return new Date(date).toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' });
}

// 今日の開始時刻（00:00:00）
function getTodayStart() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

// タスク名に対する色マップを生成
const COLOR_PALETTE = [
  '#3B82F6', '#10B981', '#F59E0B', '#EF4444',
  '#8B5CF6', '#EC4899', '#14B8A6', '#F97316',
  '#6366F1', '#84CC16',
];

function buildTaskColorMap(sessions, todayLogs, savedColors = {}) {
  const names = [...new Set([
    ...todayLogs.map(l => l.task_name),
    ...sessions.map(s => s.task_name),
  ])];
  const map = {};
  let idx = 0;
  names.forEach(name => {
    if (savedColors[name]) {
      map[name] = savedColors[name];
    } else {
      map[name] = COLOR_PALETTE[idx % COLOR_PALETTE.length];
      idx++;
    }
  });
  return map;
}

// ズームのステップ・範囲定義
const ZOOM_STEPS = [0.5, 0.75, 1, 1.5, 2, 3, 4, 6, 8];
const ZOOM_DEFAULT_IDX = 2; // 1.0x

// 時間差を「X時間Y分」形式に
function formatDuration(ms) {
  const totalMin = Math.floor(ms / 60000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return h > 0 ? `${h}時間${m}分` : `${m}分`;
}

// ========================================
// 本日のタイムラインガントチャート
// ・ユーザー名列を固定、時間軸のみ横スクロール
// ・常に 00:00〜24:00 を表示
// ・ホバー時にカスタムツールチップ表示
// ========================================
// eslint-disable-next-line no-unused-vars
function TodayGantt({ sessions, todayLogs, displayUsers, tick, savedColors = {} }) {
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(900);
  const [zoomIdx, setZoomIdx] = useState(ZOOM_DEFAULT_IDX);
  const [tooltip, setTooltip] = useState(null); // { x, y, taskName, startAt, endAt, duration }

  // コンテナ幅を ResizeObserver で監視
  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver(entries => {
      for (const entry of entries) setContainerWidth(entry.contentRect.width);
    });
    observer.observe(containerRef.current);
    setContainerWidth(containerRef.current.clientWidth);
    return () => observer.disconnect();
  }, []);

  const zoom = ZOOM_STEPS[zoomIdx];
  const now = new Date();
  const dayStart = getTodayStart();

  // 常に 00:00〜24:00
  const VIEW_START_MS = dayStart.getTime();
  const VIEW_END_MS   = dayStart.getTime() + 24 * 3600000;
  const TOTAL_MS      = 24 * 3600000;

  const taskColors = buildTaskColorMap(sessions, todayLogs, savedColors);
  const allTaskNames = Object.keys(taskColors);

  const LABEL_W = 150;  // 固定ユーザー名列の幅
  const BASE_CHART_W = Math.max(400, containerWidth - LABEL_W);
  const CHART_W = Math.round(BASE_CHART_W * zoom); // ズーム適用後の幅
  const ROW_H = 52;
  const TIME_H = 34;    // 時間軸ヘッダーの高さ
  const svgH = TIME_H + displayUsers.length * ROW_H + 1;

  const msToX = (ms) => ((ms - VIEW_START_MS) / TOTAL_MS) * CHART_W;

  // 目盛り間隔（ズームに応じて調整）
  const tickMs = zoom >= 4 ? 30 * 60000 : zoom >= 1.5 ? 3600000 : 2 * 3600000;
  const ticks = [];
  for (let ms = VIEW_START_MS; ms <= VIEW_END_MS; ms += tickMs) {
    ticks.push(new Date(ms));
  }

  const formatTick = (d) => {
    const h = String(d.getHours()).padStart(2, '0');
    const m = String(d.getMinutes()).padStart(2, '0');
    return d.getMinutes() === 0 ? `${h}:00` : `${h}:${m}`;
  };

  // ユーザーごとのセグメントを生成
  const userSegments = displayUsers.map(user => {
    const logs = todayLogs.filter(l => l.user_id === user.user_id);
    const active = sessions.find(s => s.user_id === user.user_id);
    const segs = logs.filter(l => l.end_at).map(l => ({
      startMs: new Date(l.start_at).getTime(),
      endMs:   new Date(l.end_at).getTime(),
      taskName: l.task_name,
      active: false,
    }));
    if (active) {
      segs.push({
        startMs: new Date(active.start_at).getTime(),
        endMs: now.getTime(),
        taskName: active.task_name,
        active: true,
      });
    }
    return { user, segs };
  });

  // ホバー時にツールチップを設定
  const showTooltip = (e, seg) => {
    const durationMs = seg.endMs - seg.startMs;
    setTooltip({
      x: e.clientX,
      y: e.clientY,
      taskName: seg.taskName,
      startAt: formatHHMM(seg.startMs),
      endAt: seg.active ? '--:--' : formatHHMM(seg.endMs),
      duration: seg.active
        ? `${formatDuration(durationMs)}（計測中）`
        : formatDuration(durationMs),
    });
  };
  const moveTooltip = (e) => {
    if (tooltip) setTooltip(p => p && { ...p, x: e.clientX, y: e.clientY });
  };
  const hideTooltip = () => setTooltip(null);

  if (displayUsers.length === 0) {
    return (
      <div className="today-gantt" ref={containerRef}>
        <h3>📅 本日のタイムライン</h3>
        <p className="today-gantt-empty">ユーザーを選択するか、監視を開始してください</p>
      </div>
    );
  }

  const nowX = msToX(now.getTime());

  return (
    <div className="today-gantt" ref={containerRef}>
      {/* ヘッダー（タイトル＋ズームボタン） */}
      <div className="today-gantt-header">
        <h3>📅 本日のタイムライン</h3>
        <div className="gantt-zoom-controls">
          <button
            className="gantt-zoom-btn"
            onClick={() => setZoomIdx(i => Math.max(0, i - 1))}
            disabled={zoomIdx === 0}
            title="縮小"
          >🔍−</button>
          <span className="gantt-zoom-label">×{zoom}</span>
          <button
            className="gantt-zoom-btn"
            onClick={() => setZoomIdx(i => Math.min(ZOOM_STEPS.length - 1, i + 1))}
            disabled={zoomIdx === ZOOM_STEPS.length - 1}
            title="拡大"
          >🔍+</button>
        </div>
      </div>

      {/* ボディ：左の固定ラベル列 ＋ 右のスクロールチャート列 */}
      <div className="gantt-body">
        {/* ── 固定ユーザー名列 ── */}
        <div className="gantt-label-col" style={{ width: LABEL_W }}>
          {/* 時間軸ヘッダーの高さに合わせた空白 */}
          <div className="gantt-label-spacer" style={{ height: TIME_H }} />
          {displayUsers.map((user, i) => {
            const label = user.display_name || user.email || '';
            return (
              <div
                key={user.user_id}
                className="gantt-label-cell"
                style={{
                  height: ROW_H,
                  background: i % 2 === 0 ? '#F9FAFB' : '#FFF',
                }}
              >
                {label.length > 16 ? label.slice(0, 15) + '…' : label}
              </div>
            );
          })}
        </div>

        {/* ── 横スクロールするチャート列 ── */}
        <div className="gantt-chart-scroll" onMouseMove={moveTooltip} onMouseLeave={hideTooltip}>
          <svg width={CHART_W} height={svgH}>
            {/* 時間軸ヘッダー背景 */}
            <rect x={0} y={0} width={CHART_W} height={TIME_H} fill="#fff" />

            {/* グリッド線 ＆ 時刻ラベル */}
            {ticks.map((t, i) => {
              const x = msToX(t.getTime());
              const isHour = t.getMinutes() === 0;
              const anchor = i === 0 ? 'start' : i === ticks.length - 1 ? 'end' : 'middle';
              return (
                <g key={i}>
                  <line x1={x} y1={TIME_H - 4} x2={x} y2={svgH}
                    stroke={isHour ? '#9CA3AF' : '#D1D5DB'}
                    strokeWidth={isHour ? 1 : 0.5}
                    strokeDasharray={isHour ? '4 3' : '2 4'} />
                  <text x={x} y={TIME_H - 8} textAnchor={anchor} fontSize="11" fill="#6B7280">
                    {formatTick(t)}
                  </text>
                </g>
              );
            })}

            {/* 現在時刻ライン（当日のみ） */}
            {now >= dayStart && nowX >= 0 && nowX <= CHART_W && (
              <g>
                <line x1={nowX} y1={0} x2={nowX} y2={svgH}
                  stroke="#EF4444" strokeWidth="2" strokeDasharray="5 3" />
                <text x={nowX} y={TIME_H - 8} textAnchor="middle" fontSize="11" fill="#EF4444">
                  {formatHHMM(now)}
                </text>
              </g>
            )}

            {/* ユーザー行 */}
            {userSegments.map(({ user, segs }, rowIdx) => {
              const rowY = TIME_H + rowIdx * ROW_H;
              return (
                <g key={user.user_id}>
                  {/* 行の背景 */}
                  <rect x={0} y={rowY} width={CHART_W} height={ROW_H}
                    fill={rowIdx % 2 === 0 ? '#F9FAFB' : '#FFF'} />
                  {/* 行の下線 */}
                  <line x1={0} y1={rowY + ROW_H} x2={CHART_W} y2={rowY + ROW_H}
                    stroke="#E5E7EB" strokeWidth="1" />

                  {/* 作業セグメント */}
                  {segs.map((seg, si) => {
                    const sx = Math.max(0, msToX(seg.startMs));
                    const ex = Math.min(CHART_W, msToX(seg.endMs));
                    const w  = Math.max(3, ex - sx);
                    const color = taskColors[seg.taskName] || '#6B7280';
                    return (
                      <g key={si}>
                        <rect
                          x={sx} y={rowY + 8} width={w} height={ROW_H - 16}
                          fill={color} opacity={seg.active ? 1 : 0.82} rx="4"
                          style={{ cursor: 'pointer' }}
                          onMouseEnter={(e) => showTooltip(e, seg)}
                          onMouseMove={(e) => showTooltip(e, seg)}
                          onMouseLeave={hideTooltip}
                        />
                        {w > 60 && (
                          <text
                            x={sx + 7} y={rowY + ROW_H / 2 + 5}
                            fontSize="11" fill="#fff"
                            style={{ pointerEvents: 'none', userSelect: 'none' }}
                          >
                            {seg.taskName.length > Math.floor(w / 7)
                              ? seg.taskName.slice(0, Math.floor(w / 7) - 1) + '…'
                              : seg.taskName}
                          </text>
                        )}
                      </g>
                    );
                  })}
                </g>
              );
            })}
          </svg>
        </div>
      </div>

      {/* 凡例 */}
      {allTaskNames.length > 0 && (
        <div className="today-gantt-legend">
          {allTaskNames.map(name => (
            <span key={name} className="today-gantt-legend-item">
              <span className="today-gantt-legend-color" style={{ backgroundColor: taskColors[name] }} />
              {name}
            </span>
          ))}
        </div>
      )}

      {/* ホバーツールチップ（position:fixed でスクロール位置に依存しない） */}
      {tooltip && (
        <div
          className="gantt-tooltip"
          style={{ left: tooltip.x + 16, top: tooltip.y - 10 }}
        >
          <div className="gantt-tooltip-task">{tooltip.taskName}</div>
          <div className="gantt-tooltip-row">
            <span className="gantt-tooltip-label">開始</span>
            <span className="gantt-tooltip-value">{tooltip.startAt}</span>
          </div>
          <div className="gantt-tooltip-row">
            <span className="gantt-tooltip-label">終了</span>
            <span className="gantt-tooltip-value">{tooltip.endAt}</span>
          </div>
          <div className="gantt-tooltip-row">
            <span className="gantt-tooltip-label">時間</span>
            <span className="gantt-tooltip-value">{tooltip.duration}</span>
          </div>
        </div>
      )}
    </div>
  );
}

// ========================================
// 本日のユーザー別タスク内訳テーブル
// ========================================
function TodayBreakdown({ displayUsers, todayLogs, sessions, taskColors, assignments }) {
  // ユーザーごとのタスク集計（完了ログ + アクティブセッション）
  const rows = displayUsers.map(user => {
    const userLogs = todayLogs.filter(l => l.user_id === user.user_id);
    const activeSession = sessions.find(s => s.user_id === user.user_id);
    const userAssignments = (assignments || []).filter(a => a.user_id?.trim() === user.user_id?.trim());

    const taskMap = {};

    userLogs.forEach(l => {
      const name = l.task_name || '不明';
      if (!taskMap[name]) taskMap[name] = { totalMs: 0, count: 0, completedCount: 0, assignedCount: 0 };
      taskMap[name].totalMs += new Date(l.end_at) - new Date(l.start_at);
      taskMap[name].count++;
      taskMap[name].completedCount += l.completed_count || 0;
    });

    // アクティブセッションも合算（現在進行中）
    if (activeSession) {
      const name = activeSession.task_name || '不明';
      if (!taskMap[name]) taskMap[name] = { totalMs: 0, count: 0, completedCount: 0, assignedCount: 0 };
      taskMap[name].totalMs += Date.now() - new Date(activeSession.start_at).getTime();
      taskMap[name].count++;
      taskMap[name].completedCount += activeSession.completed_count || 0;
      taskMap[name].assignedCount = Number(activeSession.assigned_count) || 0;
    }

    // 割り当てのあるタスクをtaskMapに追加（作業ログがなくても表示）
    userAssignments.forEach(asgn => {
      const name = asgn.task_name;
      if (!name) return;
      if (!taskMap[name]) taskMap[name] = { totalMs: 0, count: 0, completedCount: 0, assignedCount: 0 };
      // 割り当て数をセット（アクティブセッションから既に取得済みの場合は上書きしない）
      if (taskMap[name].assignedCount === 0) {
        taskMap[name].assignedCount = Number(asgn.assigned_count) || 0;
      }
    });

    const tasks = Object.entries(taskMap)
      .sort((a, b) => b[1].totalMs - a[1].totalMs);

    return { user, tasks };
  }).filter(r => r.tasks.length > 0);

  if (rows.length === 0) return null;

  return (
    <div className="today-breakdown">
      <h3>📊 本日のユーザー別タスク内訳</h3>
      <div className="today-breakdown-scroll">
        <table className="today-breakdown-table">
          <thead>
            <tr>
              <th className="breakdown-th-sticky">👤 ユーザー</th>
              <th>📋 タスク</th>
              <th>⏱️ 合計時間</th>
              <th>🔁 実行回数</th>
              <th>✅ 完了 / 割り当て</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ user, tasks }) =>
              tasks.map(([ taskName, data ], ti) => (
                <tr key={`${user.user_id}-${taskName}`}
                  className={ti === 0 ? 'breakdown-user-first' : 'breakdown-user-sub'}>
                  {ti === 0 ? (
                    <td className="breakdown-user-cell breakdown-td-sticky" rowSpan={tasks.length}>
                      {user.display_name || user.email || ''}
                    </td>
                  ) : null}
                  <td className="breakdown-task-cell">
                    <span className="breakdown-task-dot"
                      style={{ background: taskColors[taskName] || '#9CA3AF' }} />
                    {taskName}
                  </td>
                  <td className="breakdown-time">{formatDuration(data.totalMs)}</td>
                  <td className="breakdown-count">{data.count}回</td>
                  <td className="breakdown-count">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                      {data.assignedCount > 0
                        ? <span>
                            <span style={{ fontWeight: 700, color: '#1D4ED8' }}>{data.completedCount}</span>
                            <span style={{ color: '#9CA3AF' }}> / {data.assignedCount}</span>
                          </span>
                        : data.completedCount > 0
                          ? <span style={{ fontWeight: 700, color: '#1D4ED8' }}>{data.completedCount}</span>
                          : '—'
                      }
                      {data.assignedCount > 0 && (
                        data.completedCount >= data.assignedCount
                          ? <span className="status-badge status-badge--done">完了</span>
                          : <span className="status-badge status-badge--pending">未完了</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ========================================
// ユーザー選択ダイアログ
// ========================================
function UserSelectDialog({ allUsers, selectedUserIds, onClose, onSave }) {
  const [search, setSearch] = useState('');
  const [tempSelected, setTempSelected] = useState(new Set(selectedUserIds));

  const filtered = allUsers.filter(u => {
    const q = search.toLowerCase();
    return (
      (u.display_name || '').toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q) ||
      (u.last_name || '').toLowerCase().includes(q) ||
      (u.first_name || '').toLowerCase().includes(q)
    );
  });

  const toggle = (userId) => {
    setTempSelected(prev => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const toggleAll = () => {
    if (tempSelected.size === filtered.length && filtered.every(u => tempSelected.has(u.user_id))) {
      setTempSelected(new Set());
    } else {
      setTempSelected(new Set(filtered.map(u => u.user_id)));
    }
  };

  return (
    <div className="dialog-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="dialog-box">
        <div className="dialog-header">
          <h3>👥 監視ユーザーを選択</h3>
          <button className="dialog-close-btn" onClick={onClose}>✕</button>
        </div>

        <div className="dialog-search-wrap">
          <span className="dialog-search-icon">🔍</span>
          <input
            type="text"
            placeholder="名前・メールアドレスで検索..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="dialog-search-input"
            autoFocus
          />
          {search && (
            <button className="dialog-search-clear" onClick={() => setSearch('')}>✕</button>
          )}
        </div>

        <div className="dialog-list-header">
          <label className="dialog-check-all">
            <input
              type="checkbox"
              checked={filtered.length > 0 && filtered.every(u => tempSelected.has(u.user_id))}
              onChange={toggleAll}
            />
            <span>全選択 ({filtered.length}人)</span>
          </label>
        </div>

        <div className="dialog-user-list">
          {filtered.length === 0 ? (
            <p className="dialog-no-result">該当するユーザーがいません</p>
          ) : (
            filtered.map(user => (
              <label key={user.user_id} className="dialog-user-item">
                <input
                  type="checkbox"
                  checked={tempSelected.has(user.user_id)}
                  onChange={() => toggle(user.user_id)}
                />
                <div className="dialog-user-info">
                  <span className="dialog-user-name">
                    {user.display_name || `${user.last_name || ''} ${user.first_name || ''}`.trim() || user.email}
                  </span>
                  <span className="dialog-user-email">{user.email}</span>
                </div>
                {tempSelected.has(user.user_id) && <span className="dialog-check-mark">✓</span>}
              </label>
            ))
          )}
        </div>

        <div className="dialog-footer">
          <span className="dialog-selected-count">{tempSelected.size}人を選択中</span>
          <div className="dialog-footer-btns">
            <button className="btn-dialog-cancel" onClick={onClose}>キャンセル</button>
            <button className="btn-dialog-save" onClick={() => onSave(tempSelected)}>保存</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// localStorage のキー
const STORAGE_KEY = 'realtime_selected_user_ids';

// ========================================
// メインコンポーネント
// ========================================
function RealTimeMonitor() {
  const [isMonitoring, setIsMonitoring] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  // 選択ユーザーは localStorage から復元
  const [selectedUserIds, setSelectedUserIds] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? new Set(JSON.parse(saved)) : new Set();
    } catch {
      return new Set();
    }
  });
  const [todayLogs, setTodayLogs] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tick, setTick] = useState(0);
  const [savedColors, setSavedColors] = useState({});
  const intervalRef = useRef(null);
  const tickRef = useRef(null);

  // タスクの保存色を取得
  const fetchTaskColors = async () => {
    try {
      const op = get({ apiName: 'worktimerApi', path: '/tasks' });
      const response = await op.response;
      const data = await response.body.json();
      const map = {};
      (Array.isArray(data) ? data : []).forEach(t => {
        if (t.color) map[t.task_name] = t.color;
      });
      setSavedColors(map);
    } catch (e) {
      console.error('❌ タスク色取得エラー:', e);
    }
  };

  // 全ユーザー取得
  const fetchAllUsers = async () => {
    try {
      const op = get({ apiName: 'worktimerApi', path: '/users/all' });
      const response = await op.response;
      const data = await response.body.json();
      setAllUsers(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("❌ ユーザー取得エラー:", e);
    }
  };

  // アクティブセッション取得
  const fetchSessions = async () => {
    try {
      const op = get({ apiName: 'worktimerApi', path: '/active-sessions' });
      const response = await op.response;
      const data = await response.body.json();
      setSessions(Array.isArray(data) ? data : []);
      setError(null);
    } catch (e) {
      console.error("❌ アクティブセッション取得エラー:", e);
      setError("データの取得に失敗しました");
    }
  };

  // 本日の作業ログ取得
  const fetchTodayLogs = async () => {
    try {
      const op = get({ apiName: 'worktimerApi', path: '/worklogs/all' });
      const response = await op.response;
      const data = await response.body.json();
      const todayStr = new Date().toDateString();
      setTodayLogs(
        (Array.isArray(data) ? data : []).filter(
          l => new Date(l.start_at).toDateString() === todayStr
        )
      );
    } catch (e) {
      console.error("❌ 作業ログ取得エラー:", e);
    }
  };

  // 本日のタスク割り当て取得
  const fetchTodayAssignments = async () => {
    try {
      const today = getLocalDateString();
      const op = get({ apiName: 'worktimerApi', path: '/task-assignments', options: { queryParams: { date: today } } });
      const response = await op.response;
      const data = await response.body.json();
      setAssignments(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("❌ 割り当て取得エラー:", e);
    }
  };

  // マウント時：ユーザー一覧＋本日のタイムラインを一括取得
  useEffect(() => {
    const init = async () => {
      await Promise.all([fetchAllUsers(), fetchTodayLogs(), fetchTaskColors(), fetchTodayAssignments()]);
      setInitialLoading(false);
    };
    init();
  }, []);

  // 監視ON/OFF時にポーリングを開始/停止
  useEffect(() => {
    if (!isMonitoring) {
      clearInterval(intervalRef.current);
      clearInterval(tickRef.current);
      return;
    }

    // 監視開始直後に即時取得
    fetchSessions();
    fetchTodayLogs();
    fetchTodayAssignments();
    intervalRef.current = setInterval(() => {
      fetchSessions();
      fetchTodayLogs();
      fetchTodayAssignments();
    }, 2000);
    tickRef.current = setInterval(() => setTick(t => t + 1), 1000);

    return () => {
      clearInterval(intervalRef.current);
      clearInterval(tickRef.current);
    };
  }, [isMonitoring]);

  // selectedUserIds が変わるたびに localStorage へ保存
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify([...selectedUserIds]));
    } catch {
      // localStorage が使えない環境では無視
    }
  }, [selectedUserIds]);

  const handleMonitorToggle = () => {
    if (isMonitoring) {
      setIsMonitoring(false);
      setSessions([]);
    } else {
      setIsMonitoring(true);
    }
  };

  // 表示対象ユーザー：選択ユーザー ∪ 未選択のアクティブユーザー
  const displayUsers = [
    ...allUsers.filter(u => selectedUserIds.has(u.user_id)),
    ...sessions
      .filter(s => !selectedUserIds.has(s.user_id))
      .map(s => ({
        user_id: s.user_id,
        display_name: s.display_name,
        email: s.email,
      })),
  ];

  return (
    <div className="realtime-monitor">
      <div className="realtime-header">
        <h2>🟢 リアルタイム作業状況</h2>
        <div className="realtime-controls">
          <button
            className={isMonitoring ? 'btn-stop-monitor' : 'btn-start-monitor'}
            onClick={handleMonitorToggle}
          >
            {isMonitoring ? '⏹ 確認停止' : '▶ 確認開始'}
          </button>
          <button className="btn-select-users" onClick={() => setShowDialog(true)}>
            👥 ユーザー選択{selectedUserIds.size > 0 ? ` (${selectedUserIds.size})` : ''}
          </button>
          {isMonitoring && (
            <span className="realtime-badge">
              {sessions.length > 0 ? `${sessions.length}人 作業中` : '作業中なし'}
            </span>
          )}
        </div>
      </div>

      {error && <div className="realtime-error">{error}</div>}

      {/* 初期ローディング */}
      {initialLoading && <div className="loading">読み込み中...</div>}

      {/* 初期ロード完了後：選択ユーザーなし＆監視OFFなら案内 */}
      {!initialLoading && !isMonitoring && displayUsers.length === 0 && (
        <div className="realtime-empty">
          <p>「▶ 確認開始」ボタンを押すか、「👥 ユーザー選択」でユーザーを選んでください</p>
        </div>
      )}

      {/* ユーザーが表示対象にあれば常に表示（監視ON/OFFに関わらず） */}
      {!initialLoading && displayUsers.length > 0 && (
        <>
          {/* アクティブセッションテーブル */}
          <div className="realtime-table-wrapper">
            <table className="realtime-table">
              <thead>
                <tr>
                  <th>ユーザー名</th>
                  <th>状態</th>
                  <th>作業タスク</th>
                  <th>開始時刻</th>
                  <th>経過時間</th>
                  <th>完了数 / 割り当て</th>
                </tr>
              </thead>
              <tbody>
                {displayUsers.map(user => {
                  const session = sessions.find(s => s.user_id === user.user_id);
                  return (
                    <tr
                      key={user.user_id}
                      className={`realtime-row ${!session ? 'realtime-row-idle' : ''}`}
                    >
                      <td className="realtime-user">
                        {session && <span className="realtime-dot" />}
                        {user.display_name || user.email}
                      </td>
                      <td>
                        {isMonitoring
                          ? session
                            ? <span className="status-active">作業中</span>
                            : <span className="status-idle">待機中</span>
                          : <span className="status-idle">—</span>
                        }
                      </td>
                      <td className="realtime-task">{session ? session.task_name : '—'}</td>
                      <td className="realtime-start">
                        {session
                          ? new Date(session.start_at).toLocaleTimeString('ja-JP', {
                              hour: '2-digit', minute: '2-digit', second: '2-digit',
                            })
                          : '—'
                        }
                      </td>
                      <td className="realtime-elapsed">
                        {session ? formatElapsed(session.start_at) : '—'}
                      </td>
                      <td className="realtime-count-cell">
                        {session ? (
                          <span className="realtime-count-badge">
                            <span className="realtime-count-done">{session.completed_count || 0}</span>
                            {Number(session.assigned_count) > 0 && (
                              <span className="realtime-count-total"> / {session.assigned_count}</span>
                            )}
                          </span>
                        ) : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* 本日のガントチャート */}
          <TodayGantt
            sessions={sessions}
            todayLogs={todayLogs}
            displayUsers={displayUsers}
            tick={tick}
            savedColors={savedColors}
          />

          {/* 本日のユーザー別タスク内訳 */}
          <TodayBreakdown
            displayUsers={displayUsers}
            todayLogs={todayLogs}
            sessions={sessions}
            taskColors={buildTaskColorMap(sessions, todayLogs, savedColors)}
            assignments={assignments}
          />
        </>
      )}

      {/* ユーザー選択ダイアログ */}
      {showDialog && (
        <UserSelectDialog
          allUsers={allUsers}
          selectedUserIds={selectedUserIds}
          onClose={() => setShowDialog(false)}
          onSave={(newSelected) => {
            setSelectedUserIds(newSelected);
            setShowDialog(false);
          }}
        />
      )}
    </div>
  );
}

export default RealTimeMonitor;
